import { X } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

export interface BrandVoiceDraft {
	tone: string;
	traits: string[];
	values: string[];
}

/**
 * Tone of voice: how the brand sounds, plus the two word lists.
 *
 * Traits and values are `[String!]`, so they are edited as words rather than a
 * comma-separated line — a line would make "sustainable, honest" one trait the
 * moment someone forgets the field is a list.
 */
export function EditBrandVoiceModal({
	open,
	onOpenChange,
	voice,
	onSave,
	loading,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	voice: { tone?: string | null; traits?: string[] | null; values?: string[] | null };
	onSave: (draft: BrandVoiceDraft) => void;
	loading?: boolean;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>Edit tone of voice</DialogTitle>
					<DialogDescription>
						How the brand sounds, and the words it stands for.
					</DialogDescription>
				</DialogHeader>

				{/* Mounted by opening, so the draft starts from the brand as it is
				    now — see `EditBrandStoryModal` for why this isn't an effect. */}
				{open && (
					<VoiceForm
						voice={voice}
						onCancel={() => onOpenChange(false)}
						onSave={onSave}
						loading={loading}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

function VoiceForm({
	voice,
	onCancel,
	onSave,
	loading,
}: {
	voice: { tone?: string | null; traits?: string[] | null; values?: string[] | null };
	onCancel: () => void;
	onSave: (draft: BrandVoiceDraft) => void;
	loading?: boolean;
}) {
	const [tone, setTone] = useState(voice.tone ?? '');
	const [traits, setTraits] = useState<string[]>(voice.traits ? [...voice.traits] : []);
	const [values, setValues] = useState<string[]>(voice.values ? [...voice.values] : []);

	return (
		<DialogForm onSubmit={() => onSave({ tone: tone.trim(), traits, values })}>
			<DialogBody>
				<div className="flex flex-col gap-5">
					<Field label="How we sound">
						<Textarea
							value={tone}
							onChange={(e) => setTone(e.target.value)}
							rows={6}
							placeholder="e.g. Warm and direct. We write in short sentences and never use jargon."
						/>
					</Field>

					<WordsField
						label="Traits"
						hint="Adjectives that describe the brand."
						placeholder="e.g. Confident"
						words={traits}
						onChange={setTraits}
					/>

					<WordsField
						label="Values"
						hint="What the brand stands for."
						placeholder="e.g. Sustainability"
						words={values}
						onChange={setValues}
					/>
				</div>
			</DialogBody>

			<DialogFooter>
				<Button type="button" variant="outline" onClick={onCancel}>
					Cancel
				</Button>
				<Button type="submit" disabled={loading}>
					{loading ? 'Saving...' : 'Save'}
				</Button>
			</DialogFooter>
		</DialogForm>
	);
}

/**
 * A list of short words, added one at a time.
 *
 * Enter and comma both commit, because both are what people reach for. The
 * Enter handler stops the event rather than letting it reach the form — inside
 * a dialog form, Enter would otherwise submit the whole thing while the user
 * meant "and the next word".
 */
function WordsField({
	label,
	hint,
	placeholder,
	words,
	onChange,
}: {
	label: string;
	hint: string;
	placeholder: string;
	words: string[];
	onChange: (words: string[]) => void;
}) {
	const [draft, setDraft] = useState('');

	const commit = (raw: string) => {
		const word = raw.trim().replace(/,+$/, '').trim();
		setDraft('');
		if (!word) return;
		/* Case-insensitive, so "Bold" and "bold" don't both end up as chips. */
		if (words.some((w) => w.toLowerCase() === word.toLowerCase())) return;
		onChange([...words, word]);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			commit(draft);
			return;
		}
		/* Backspace on an empty box removes the last chip — the usual way out of
		   a tag field when the mouse is elsewhere. */
		if (e.key === 'Backspace' && draft === '' && words.length > 0) {
			onChange(words.slice(0, -1));
		}
	};

	return (
		<Field label={label}>
			<div className="flex flex-col gap-2">
				{words.length > 0 && (
					<div className="flex flex-wrap gap-1.5">
						{words.map((word) => (
							<span
								key={word}
								className="inline-flex items-center gap-1 rounded-full border border-line-2 bg-surface-fill px-2.5 py-0.5 text-xs text-text-2"
							>
								{word}
								<button
									type="button"
									title={`Remove ${word}`}
									onClick={() => onChange(words.filter((w) => w !== word))}
									className="bg-transparent text-text-4 transition-colors hover:text-text-1"
								>
									<X size={11} />
								</button>
							</span>
						))}
					</div>
				)}
				<Input
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={handleKeyDown}
					/* Committing on blur too, so a word typed and then clicked
					   away from isn't lost on save. */
					onBlur={() => commit(draft)}
					placeholder={placeholder}
				/>
				<p className="text-xs text-text-4">{hint} Press Enter to add.</p>
			</div>
		</Field>
	);
}
